/* Factumis App — primitives & composants partagés (TS/React).

   Composants exportés :
   - Button        : bouton polymorphe (primary/ghost/soft/danger, icon, loading).
   - LIFECYCLE     : mapping statut B2Brouter → libellé FR + sémantique de couleur.
   - StatusPill    : badge coloré de statut (utilise LIFECYCLE).
   - KPI           : carte de KPI avec valeur, tendance ou action.
   - Field         : wrapper label+champ avec accessibilité (aria-describedby auto).
   - Input/Select  : contrôles HTML stylisés (classe CSS `ctrl`).
   - Timeline      : jalons (done/current/future) avec rail vertical.
   - Drawer        : panneau latéral glissant.
   - Modal         : boîte de dialogue centrée.
   - Illus/EmptyState : illustration SVG générative + état vide.
   - ConfBanner    : bannière de conformité/alerte (ok/warn/err).
   - ConfirmDialog : confirmation modale (remplace window.confirm).
   - eur()/eurAmount() : formatage monétaire FR (ex. 1 234,56 €), unique dans l'app. */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { Fragment, cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react';
import { FxIcons, Icon, type IconName } from '../lib/icons';

/* ---------- Button ---------- */
type ButtonProps = {
  variant?: 'primary' | 'ghost' | 'soft' | 'danger';
  size?: 'sm' | 'lg';
  icon?: IconName;
  iconRight?: IconName;
  full?: boolean;
  loading?: boolean;
  children?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({ variant = 'primary', size, icon, iconRight, full, loading, children, ...rest }: ButtonProps) {
  const cls = ['btn', 'btn--' + variant];
  if (size) cls.push('btn--' + size);
  if (full) cls.push('btn--full');
  if (!children && icon) cls.push('btn--icon');
  return (
    <button className={cls.join(' ')} {...rest}>
      {loading ? <span className="spin" /> : icon ? <Icon name={icon} size={17} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={17} /> : null}
    </button>
  );
}

/* ---------- Invoice lifecycle (B2Brouter → libellés FR) ---------- */
export type LifecycleKind = 'neutral' | 'progress' | 'positive' | 'negative' | 'inactive';
export const LIFECYCLE: Record<string, { label: string; kind: LifecycleKind }> = {
  new: { label: 'Brouillon', kind: 'neutral' },
  pending: { label: 'À envoyer', kind: 'progress' },
  processing: { label: "En cours d'envoi", kind: 'progress' },
  sent: { label: 'Envoyée', kind: 'progress' },
  received: { label: 'Reçue par le client', kind: 'positive' },
  approved: { label: 'Acceptée', kind: 'positive' },
  paid: { label: 'Payée', kind: 'positive' },
  rejected: { label: 'Refusée', kind: 'negative' },
  overdue: { label: 'En retard', kind: 'negative' },
  litige: { label: 'En litige', kind: 'negative' },
  error: { label: 'Erreur', kind: 'negative' },
  cancelled: { label: 'Annulée', kind: 'inactive' },
  // factures reçues
  tovalidate: { label: 'À valider', kind: 'progress' },
  validated: { label: 'Validée', kind: 'positive' },
  awaitpay: { label: 'En attente de paiement', kind: 'progress' },
  // devis
  draft: { label: 'Brouillon', kind: 'neutral' },
  qsent: { label: 'Envoyé', kind: 'progress' },
  accepted: { label: 'Accepté', kind: 'positive' },
  refused: { label: 'Refusé', kind: 'negative' },
  expired: { label: 'Expiré', kind: 'inactive' },
  // factures récurrentes
  active: { label: 'Active', kind: 'positive' },
  paused: { label: 'En pause', kind: 'inactive' },
  // e-reporting b2c (draft réutilise déjà l'entrée « devis » ci-dessus)
  submitted: { label: 'Transmise', kind: 'positive' },
};

export function StatusPill({ status }: { status: string }) {
  const s = LIFECYCLE[status] || { label: status, kind: 'neutral' as LifecycleKind };
  return (
    <span className={'pill pill--' + s.kind}>
      <span className="dot" />
      {s.label}
    </span>
  );
}

/* ---------- KPI card ---------- */
type KPIProps = {
  k: ReactNode;
  value: ReactNode;
  cur?: string;
  trend?: ReactNode;
  trendDir?: 'up' | 'down' | 'flat';
  alert?: boolean;
  badge?: ReactNode;
  action?: ReactNode;
  onAction?: () => void;
};
export function KPI({ k, value, cur, trend, trendDir, alert, badge, action, onAction }: KPIProps) {
  return (
    <div className={'kpi' + (alert ? ' kpi--alert' : '')}>
      {badge ? <span className="kpi__badge">{badge}</span> : null}
      <div className="kpi__k">
        {alert ? <span className="kpi__dot" /> : null}
        {k}
      </div>
      <div className="kpi__v">
        {value}
        {/* Espace insécable : avec une espace ordinaire, le « € » retombait seul
            sur une ligne sous la valeur en mobile (constat d'audit QW1). */}
        {cur ? <span className="cur">{'\u00A0' + cur}</span> : null}
      </div>
      {action ? (
        <button className="kpi__act" onClick={onAction}>
          {action}
          <span className="kpi__act-arr">→</span>
        </button>
      ) : trend ? (
        <div className={'kpi__tr ' + (trendDir || 'flat')}>
          {trendDir === 'up' ? <Icon name="arrow-up" size={13} /> : trendDir === 'down' ? <Icon name="arrow-down" size={13} /> : null}
          {trend}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- Field primitives ---------- */
type FieldProps = {
  label?: ReactNode;
  opt?: string;
  help?: ReactNode;
  error?: ReactNode;
  ok?: ReactNode;
  children?: ReactNode;
};
export function Field({ label, opt, help, error, ok, children }: FieldProps) {
  const cls = ['field'];
  if (error) cls.push('err');
  if (ok && !error) cls.push('ok');

  // Accessibilité : relier <label> ↔ champ et le message d'aide/erreur via
  // aria-describedby. On injecte id + aria-* sur l'enfant SI c'est un élément
  // (input/select/textarea direct) qui ne porte pas déjà d'id. Un enfant
  // composite (wrapper div) reçoit l'id sans casse — l'association est alors
  // partielle, à traiter au cas par cas.
  const autoId = useId();
  const msgId = `${autoId}-msg`;
  const hasMsg = Boolean(error || ok || help);
  let controlId: string | undefined;
  let control: ReactNode = children;
  if (isValidElement(children)) {
    const childProps = children.props as Record<string, unknown>;
    controlId = (childProps.id as string | undefined) ?? autoId;
    control = cloneElement(children as ReactElement, {
      id: controlId,
      'aria-describedby': hasMsg
        ? [childProps['aria-describedby'] as string | undefined, msgId].filter(Boolean).join(' ')
        : (childProps['aria-describedby'] as string | undefined),
      'aria-invalid': error ? true : (childProps['aria-invalid'] as boolean | undefined),
    } as Partial<Record<string, unknown>>);
  }

  return (
    <div className={cls.join(' ')}>
      {label ? (
        <label htmlFor={controlId}>
          {label}
          {opt ? <span className="opt">, {opt}</span> : null}
        </label>
      ) : null}
      {control}
      {error ? (
        <div className="field__msg" id={msgId}>
          <Icon name="alert" size={14} />
          {error}
        </div>
      ) : ok ? (
        <div className="field__ok" id={msgId}>
          <Icon name="check-c" size={14} />
          {ok}
        </div>
      ) : help ? (
        <div className="field__help" id={msgId}>{help}</div>
      ) : null}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="ctrl" {...props} />;
}
export function Select({ children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className="ctrl" {...rest}>
      {children}
    </select>
  );
}

/* ---------- Timeline ---------- */
export type TimelineStep = { label: ReactNode; when?: ReactNode; state: 'done' | 'current' | 'future' };
export function Timeline({ steps }: { steps: TimelineStep[] }) {
  return (
    <div className="tline">
      {steps.map((st, i) => (
        <div key={i} className={'tline__step ' + st.state}>
          <div className="tline__rail">
            <span className="tline__dot">{st.state === 'done' ? <Icon name="check" size={10} /> : null}</span>
            <span className="tline__line" />
          </div>
          <div className="tline__body">
            <b>{st.label}</b>
            {st.when ? <div className="when">{st.when}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Drawer ---------- */
type DrawerProps = { open: boolean; onClose: () => void; title?: ReactNode; children?: ReactNode; footer?: ReactNode };
export function Drawer({ open, onClose, title, children, footer }: DrawerProps) {
  return (
    <Fragment>
      <div className={'drawer-scrim' + (open ? ' on' : '')} onClick={onClose} />
      <aside className={'drawer' + (open ? ' on' : '')} role="dialog" aria-hidden={!open}>
        <div className="drawer__hd">
          <b style={{ fontSize: 16, fontWeight: 600, flex: 1 }}>{title}</b>
          <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={onClose} />
        </div>
        <div className="drawer__body">{children}</div>
        {footer ? (
          <div style={{ padding: '16px 22px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10 }}>{footer}</div>
        ) : null}
      </aside>
    </Fragment>
  );
}

/* ---------- NumField : saisie numérique non destructive ----------
   Un <input value={number}> contrôlé force la valeur à chaque frappe → impossible
   de taper « 1,5 », de vider le champ, ou d'avoir un état intermédiaire. NumField
   garde un buffer texte LOCAL pendant l'édition (virgule acceptée, champ vide OK)
   et ne remonte qu'un nombre parsé ; au blur, il resynchronise sur la valeur canonique. */
type NumFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: number;
  onChange: (n: number) => void;
};
export function NumField({ value, onChange, onBlur, ...rest }: NumFieldProps) {
  const [buf, setBuf] = useState<string | null>(null);
  const shown = buf ?? (value === 0 ? '' : String(value));
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={shown}
      onChange={(e) => {
        const raw = e.target.value;
        setBuf(raw);
        const n = parseFloat(raw.replace(',', '.'));
        onChange(Number.isFinite(n) ? n : 0);
      }}
      onBlur={(e) => {
        setBuf(null); // resync sur la valeur numérique canonique
        onBlur?.(e);
      }}
    />
  );
}

/* ---------- OptNumField : variante optionnelle de NumField ----------
   Même buffer texte local, mais la valeur canonique est number | null : null
   représente « non saisi » (champ vide, jamais envoyé au serveur), alors qu'un 0
   tapé explicitement reste un 0 volontaire (distinction requise par les champs
   espèces/carte de l'e-reporting, où 0 est une déclaration légitime). */
type OptNumFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  value: number | null;
  onChange: (n: number | null) => void;
};
export function OptNumField({ value, onChange, onBlur, ...rest }: OptNumFieldProps) {
  const [buf, setBuf] = useState<string | null>(null);
  const shown = buf ?? (value === null ? '' : String(value));
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={shown}
      onChange={(e) => {
        const raw = e.target.value;
        setBuf(raw);
        if (raw.trim() === '') {
          onChange(null);
          return;
        }
        const n = parseFloat(raw.replace(',', '.'));
        onChange(Number.isFinite(n) ? n : null);
      }}
      onBlur={(e) => {
        setBuf(null); // resync sur la valeur numérique canonique
        onBlur?.(e);
      }}
    />
  );
}

/* ---------- Modal ----------
   Accessible : aria-modal + aria-labelledby (titre), focus déplacé dans la modale
   à l'ouverture et piégé (Tab cyclique), fermeture par Échap, focus restitué au
   déclencheur à la fermeture. (WCAG 2.1.2 / 2.4.3 / 4.1.2.) */
type ModalProps = { open: boolean; onClose?: () => void; title?: ReactNode; children?: ReactNode; wide?: boolean };
export function Modal({ open, onClose, title, children, wide }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const dialog = ref.current;
    const prevFocus = document.activeElement as HTMLElement | null;
    const focusables = (): HTMLElement[] =>
      dialog
        ? Array.from(
            dialog.querySelectorAll<HTMLElement>(
              'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => el.offsetParent !== null)
        : [];
    (focusables()[0] ?? dialog)?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (f.length === 0) {
        e.preventDefault();
        return;
      }
      const idx = f.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && idx <= 0) {
        e.preventDefault();
        f[f.length - 1]!.focus();
      } else if (!e.shiftKey && idx === f.length - 1) {
        e.preventDefault();
        f[0]!.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      prevFocus?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="modal-scrim"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose?.();
      }}
    >
      <div
        className={wide ? 'modal modal--wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        ref={ref}
      >
        {title ? <h3 id={titleId}>{title}</h3> : null}
        {children}
      </div>
    </div>
  );
}

/* ---------- ConfirmDialog ----------
   Confirmation (ou simple accusé) posée sur Modal, donc avec le piège de focus,
   la fermeture par Échap et la restitution du focus déjà en place. Remplace les
   window.confirm / window.alert natifs, qui s'affichaient en haut de l'écran
   avec l'URL du site, sans style, et cassaient l'impression de produit fini.

   Sans `onConfirm`, la boîte est un accusé : un seul bouton, rien à annuler.
   Avec `onConfirm`, l'annulation est toujours offerte et l'intitulé de l'action
   décrit ce qu'elle fait (« Transmettre », « Supprimer »), jamais « OK ».
   `tone="danger"` pour une action destructive ou définitive. */
type ConfirmDialogProps = {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmIcon?: IconName;
  /** Confirmation refusée tant qu'un préalable manque (ex. facture non conforme). */
  confirmDisabled?: boolean;
  /** Infobulle du bouton d'action, pour dire ce qui bloque quand il est désactivé. */
  confirmTitle?: string;
  tone?: 'default' | 'danger';
  busy?: boolean;
  onConfirm?: () => void;
  onClose: () => void;
};
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  confirmIcon,
  confirmDisabled,
  confirmTitle,
  tone = 'default',
  busy,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const isConfirm = Boolean(onConfirm);
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={title}>
      {typeof children === 'string' ? <p>{children}</p> : children}
      <div className="acts">
        {isConfirm ? (
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
        ) : null}
        <Button
          variant={isConfirm && tone === 'danger' ? 'danger' : 'primary'}
          icon={confirmIcon}
          disabled={busy || confirmDisabled}
          loading={busy}
          title={confirmTitle}
          onClick={() => (onConfirm ? onConfirm() : onClose())}
        >
          {isConfirm ? confirmLabel : 'J’ai compris'}
        </Button>
      </div>
    </Modal>
  );
}

/* ---------- Empty state with abstract illustration ---------- */
export function Illus({ glyph = 'file-out', id }: { glyph?: IconName; id?: string }) {
  const pid = 'gp' + (id || glyph);
  const html =
    `<svg width="140" height="116" viewBox="0 0 140 116" fill="none" xmlns="http://www.w3.org/2000/svg">` +
    `<defs><pattern id="${pid}" width="11" height="11" patternUnits="userSpaceOnUse">` +
    `<circle cx="1.2" cy="1.2" r="1.2" fill="currentColor" opacity="0.14"/></pattern></defs>` +
    `<rect x="2" y="10" width="136" height="96" rx="14" fill="url(#${pid})"/>` +
    `<circle cx="70" cy="56" r="40" fill="var(--blue)" opacity="0.08"/>` +
    `<circle cx="70" cy="56" r="40" stroke="var(--blue)" stroke-width="1.5" stroke-dasharray="3 5" opacity="0.45"/>` +
    `<rect x="48" y="34" width="44" height="48" rx="7" fill="var(--surface-card)" stroke="var(--blue)" stroke-width="1.6"/>` +
    `<g transform="translate(58,46)" stroke="var(--blue)" stroke-width="1.6" stroke-linecap="round" fill="none" opacity="0.9">` +
    ((FxIcons as Record<string, string>)[glyph] || '') +
    `</g>` +
    `<circle cx="104" cy="30" r="6" fill="var(--blue)" opacity="0.55"/>` +
    `<circle cx="34" cy="82" r="4" fill="var(--blue)" opacity="0.3"/>` +
    `</svg>`;
  return <span className="illus" style={{ display: 'inline-block', width: 140, height: 116 }} dangerouslySetInnerHTML={{ __html: html }} />;
}

type EmptyStateProps = { glyph?: IconName; id?: string; title: ReactNode; children?: ReactNode; actions?: ReactNode };
export function EmptyState({ glyph, id, title, children, actions }: EmptyStateProps) {
  return (
    <div className="empty">
      <Illus glyph={glyph} id={id} />
      <h3>{title}</h3>
      <p>{children}</p>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

/* ---------- Conformity banner (bloc d'emphase sombre) ---------- */
type ConfBannerProps = {
  variant?: 'ok' | 'warn' | 'err';
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: { k: ReactNode; v: ReactNode }[];
  action?: ReactNode;
};
export function ConfBanner({ variant = 'ok', title, subtitle, meta, action }: ConfBannerProps) {
  const icon: IconName = variant === 'ok' ? 'shield' : variant === 'warn' ? 'bell' : 'alert';
  return (
    <div className={'confbar' + (variant !== 'ok' ? ' confbar--' + variant : '')}>
      <div className="confbar__ic">
        <Icon name={icon} size={22} />
      </div>
      <div className="confbar__txt">
        <b>{title}</b>
        <span>{subtitle}</span>
      </div>
      <div className="confbar__meta">
        {(meta || []).map((m, i) => (
          <div key={i} className="confbar__metaitem">
            <div className="k">{m.k}</div>
            <div className="v">
              <span className="dot pulse-dot" />
              {m.v}
            </div>
          </div>
        ))}
        {action || null}
      </div>
    </div>
  );
}

/* ---------- Ligne cliquable accessible ----------
   Une `<tr onClick>` (ou `<div className="mrow" onClick>`) n'est ni focusable ni
   activable au clavier. `rowActivation(fn)` renvoie les props à étaler sur la
   ligne pour la rendre équivalente à un bouton : focus au Tab, activation par
   Entrée/Espace, rôle annoncé aux lecteurs d'écran. (WCAG 2.1.1 / 4.1.2.) */
export function rowActivation(onActivate: () => void) {
  return {
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  } as const;
}

/* ---------- Combobox accessible (autocomplete) ----------
   Mutualise le motif WAI-ARIA 1.2 « combobox + listbox » pour les champs de
   recherche à liste déroulante (annuaire Sirene…). L'appelant garde la maîtrise
   de son état (résultats, ouverture, sélection) ; ce hook ajoute la navigation
   clavier (↑/↓/Entrée/Échap/Home/End), l'index actif et les attributs ARIA
   (role, aria-expanded, aria-activedescendant, aria-selected). À étaler :
   `inputProps` sur le champ, `listProps` sur le <ul>, `optionProps(i)` sur chaque
   <li>. La sélection souris (onMouseDown) reste à la charge de l'appelant. */
export function useCombobox<T>(opts: {
  open: boolean;
  items: T[];
  onSelect: (item: T, index: number) => void;
  onClose?: () => void;
}) {
  const { open, items, onSelect, onClose } = opts;
  const baseId = useId();
  const listId = `${baseId}-list`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;
  const [active, setActive] = useState(-1);

  // Réinitialise le surlignage quand la liste change ou que le menu se ferme.
  useEffect(() => {
    setActive(-1);
  }, [open, items]);

  function onKeyDown(e: ReactKeyboardEvent) {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        onClose?.();
      }
      return;
    }
    if (!open || items.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((a) => (a + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActive(0);
        break;
      case 'End':
        e.preventDefault();
        setActive(items.length - 1);
        break;
      case 'Enter':
        if (active >= 0 && active < items.length) {
          e.preventDefault();
          onSelect(items[active]!, active);
        }
        break;
    }
  }

  const inputProps = {
    role: 'combobox',
    'aria-expanded': open && items.length > 0,
    'aria-controls': listId,
    'aria-autocomplete': 'list',
    'aria-activedescendant': active >= 0 ? optionId(active) : undefined,
    onKeyDown,
  } as const;
  const listProps = { role: 'listbox', id: listId } as const;
  const optionProps = (i: number) =>
    ({
      role: 'option',
      id: optionId(i),
      'aria-selected': i === active,
      onMouseEnter: () => setActive(i),
    }) as const;

  return { active, inputProps, listProps, optionProps };
}

/* ---------- format helpers ----------
   Point de passage UNIQUE des montants affichés : fr-FR, 2 décimales, espaces
   insécables. Intl produit U+202F (espace fine insécable) comme séparateur de
   milliers ; cette espace est absente de la fonte Geist, si bien que le même
   montant s'affichait « 1440,00 € » ici et « 1 440,00 € » deux lignes plus bas
   (constat d'audit UX du 3 août 2026). On normalise sur U+00A0, présent dans
   toutes les fontes, y compris devant le symbole € pour qu'il ne retombe jamais
   seul en fin de ligne. */
const NBSP = '\u00A0';

/** Montant sans symbole, pour les affichages qui portent l'unité à côté (KPI). */
export function eurAmount(n: number): string {
  return n
    .toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .replace(/[\u202F\u2009\u00A0 ]/g, NBSP);
}

/** Montant complet, symbole compris (ex. « 1 234,56 € »). */
export function eur(n: number): string {
  return eurAmount(n) + NBSP + '€';
}
